import logoAnimation from 'assets/loadingAnimation/logo-animation.lottie';
import { HalfMask } from 'components/AppLoader';
import FlexContainer from 'components/FlexContainer';
import FullMask from 'components/FullMask';
import LocalStorageKey from 'config/localStorageKey';
import { getCurrentModelLanguageAndCountry } from 'features/aiWriter/store/selectors';
import { getStoredUserTonality } from 'features/aiWriter/tonality/getStoredUserTonality';
import { getEmbeddingModelsByLanguageAndAudience } from 'features/embeddingModels/store/selectors';
import getModelInitValue from 'features/embeddingModels/store/utils/getModelInitValue';
import { LottieComponent } from 'features/lottie/LottieComponent';
import { useEnableSidebarOnMount } from 'features/pageSidebar/useSidebarStore';
import BlogPostBuilder from 'features/workflowBlogPostBuilder/BlogPostBuilder';
import { FormValues } from 'features/workflowBlogPostBuilder/types';
import { Form, Formik } from 'formik';
import { noop } from 'lodash';
import { useAppSelector } from 'store/hooks';
import styled from 'styled-components';

export const BlogPostBuilderPage = () => {
  useEnableSidebarOnMount({ collapse: true });

  const models = useAppSelector(getEmbeddingModelsByLanguageAndAudience);
  const defaultLanguage = getModelInitValue(models, LocalStorageKey.AiWriterModel);
  const { language: onboardingLanguage } = useAppSelector(state => state.aiWriter.builder.blogPost);
  const { currentModelLanguage, currentModelCountry } = useAppSelector(
    getCurrentModelLanguageAndCountry
  );
  const currentLanguageModel = models.find(
    model => model.language === currentModelLanguage && model.country === currentModelCountry
  );

  const userTonality = getStoredUserTonality();

  const onboardingModel = onboardingLanguage
    ? models.find(
        model =>
          model.language === onboardingLanguage.language &&
          model.country === onboardingLanguage.country
      )
    : undefined;
  const startedFromOnboarding = onboardingModel !== undefined;

  const initialValues: FormValues = {
    title: '',
    language: currentLanguageModel?.id ?? onboardingModel?.id ?? defaultLanguage?.id ?? '',
    systemTonality: [],
    userTonality: userTonality ?? [],
    information: undefined,
    brandVoiceId: undefined,
    targetAudienceId: undefined,
    elements: [],
    goal: '',
    keywords: [],
    outline: '',
    generatedContent: [],
    disableNextStep: false,
    startedFromOnboarding
  };

  // We need the embedding models to be loaded before we can render the builder
  if (models.length === 0) {
    return (
      <Container>
        <FullMask style={{ opacity: 1 }} initial={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <HalfMask>
            <LottieComponent src={logoAnimation} options={{ speed: 1.5 }} />
          </HalfMask>
        </FullMask>
      </Container>
    );
  }

  return (
    <Formik<FormValues>
      validateOnChange
      validateOnBlur
      onSubmit={noop}
      initialValues={initialValues}
    >
      <FullSizedForm>
        <Container direction="row">
          <BlogPostBuilder />
        </Container>
      </FullSizedForm>
    </Formik>
  );
};

const FullSizedForm = styled(Form)`
  height: 100%;
`;

const Container = styled(FlexContainer)`
  height: 100%;
`;
